// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
//
// Connect / manage flows for the Бриф "Каналы" screen, rendered as MOBILE-FIRST
// bottom sheets (full-width sheet < 768px; centered ~440px modal on desktop).
// Wires the Phase-2 channelsApi (promo/channelsApi.ts) — reads are soft, writes
// are strict — without touching the warm Бриф visual design.
//
// Exports:
//   • <ConnectSheet platform onClose onDone />  — routes to the right flow:
//       - openai            → Codex device flow (start → poll 3s, 15-min cap)
//       - telegram          → 3-step wizard (validate token → channels → setup)
//       - twitter           → OAuth popup (summio-publisher-oauth postMessage)
//       - buffer (synthetic) → read-only Buffer channel list
//       - credential platforms (cookie/API key) → connectPlatform + validateCookies
//   • useConnectSheet()  — small helper the Каналы screen can call to open a sheet.
//
// We OWN this file only. We import symbols from promo/channelsApi.ts and
// promo/types.ts; we never edit them.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {
  bufferChannels,
  bufferOrganizations,
  BufferRateLimitError,
  codexDeviceStart,
  codexDeviceStatus,
  connectPlatform,
  disconnectPlatform,
  oauthStartUrl,
  telegramAddChannel,
  telegramGetChat,
  telegramSetupBot,
  telegramValidateToken,
  validateCookies,
  type CodexDeviceStartResult,
  type OAuthProvider,
  type TelegramSetupChannel,
} from '../promo/channelsApi';
import type {
  BufferChannel,
  BufferOrganization,
  ConnectionPlatform,
  Platform,
} from '../promo/types';
import { C, mono, SANS } from '../theme';

// ── Sheet target ─────────────────────────────────────────────────

/**
 * What a sheet can connect. `'buffer'` is a synthetic target (Buffer is not a
 * ConnectionPlatform — it's a read-only channel directory), the rest map to a
 * real connection slot.
 */
export type SheetPlatform = ConnectionPlatform | 'buffer';

/** Credential-form field descriptor. */
interface CredentialField {
  key: string;
  label: string;
  placeholder?: string;
  hint?: string;
  multiline?: boolean;
  secret?: boolean;
}

/** A credential platform = a flat form posted to connectPlatform. */
interface CredentialConfig {
  fields: CredentialField[];
  /** Whether `/:platform/validate-cookies` is meaningful after saving. */
  canValidate: boolean;
  note?: string;
}

// Cookie/API-key platforms. Twitter goes through OAuth (handled separately), but
// also supports a manual cookie paste as a fallback path here.
const CREDENTIAL_CONFIGS: Partial<Record<ConnectionPlatform, CredentialConfig>> = {
  twitter: {
    canValidate: true,
    note: 'Вставьте cookie-сессию X (auth_token, ct0). Или подключите через OAuth.',
    fields: [
      { key: 'auth_token', label: 'auth_token', placeholder: 'значение cookie auth_token', secret: true },
      { key: 'ct0', label: 'ct0', placeholder: 'значение cookie ct0', secret: true },
    ],
  },
  instagram: {
    canValidate: true,
    note: 'Cookie-сессия Instagram (sessionid).',
    fields: [{ key: 'sessionid', label: 'sessionid', placeholder: 'значение cookie sessionid', secret: true }],
  },
  threads: {
    canValidate: true,
    note: 'Cookie-сессия Threads (sessionid).',
    fields: [{ key: 'sessionid', label: 'sessionid', placeholder: 'значение cookie sessionid', secret: true }],
  },
  reddit: {
    canValidate: true,
    note: 'API-доступ Reddit.',
    fields: [
      { key: 'client_id', label: 'client_id', placeholder: 'app client id' },
      { key: 'client_secret', label: 'client_secret', placeholder: 'app secret', secret: true },
      { key: 'username', label: 'Логин', placeholder: 'reddit username' },
      { key: 'password', label: 'Пароль', placeholder: '••••••••', secret: true },
    ],
  },
  producthunt: {
    canValidate: false,
    note: 'Токен разработчика Product Hunt.',
    fields: [{ key: 'developer_token', label: 'developer_token', placeholder: 'API-токен', secret: true }],
  },
  linkedin: {
    canValidate: true,
    note: 'Cookie-сессия LinkedIn (li_at).',
    fields: [{ key: 'li_at', label: 'li_at', placeholder: 'значение cookie li_at', secret: true }],
  },
  facebook: {
    canValidate: true,
    note: 'Cookie-сессия Facebook.',
    fields: [{ key: 'cookies', label: 'Cookies', placeholder: 'c_user=…; xs=…', multiline: true, secret: true }],
  },
  youtube: {
    canValidate: false,
    note: 'OAuth-токен YouTube Data API.',
    fields: [{ key: 'access_token', label: 'access_token', placeholder: 'токен доступа', secret: true }],
  },
  tiktok: {
    canValidate: false,
    note: 'Cookie-сессия TikTok (sessionid).',
    fields: [{ key: 'sessionid', label: 'sessionid', placeholder: 'значение cookie sessionid', secret: true }],
  },
  gemini: {
    canValidate: false,
    note: 'Ключ Google AI (Gemini).',
    fields: [{ key: 'api_key', label: 'API-ключ', placeholder: 'AI… ключ', secret: true }],
  },
};

const PLATFORM_LABEL: Record<SheetPlatform, string> = {
  telegram: 'Telegram',
  twitter: 'X / Twitter',
  instagram: 'Instagram',
  threads: 'Threads',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  reddit: 'Reddit',
  producthunt: 'Product Hunt',
  openai: 'OpenAI · Codex',
  gemini: 'Gemini',
  buffer: 'Buffer',
};

const PLATFORM_GLYPH: Record<SheetPlatform, string> = {
  telegram: 'TG',
  twitter: 'X',
  instagram: 'IG',
  threads: 'TH',
  facebook: 'FB',
  linkedin: 'IN',
  youtube: 'YT',
  tiktok: 'TT',
  reddit: 'RD',
  producthunt: 'PH',
  openai: 'AI',
  gemini: 'GM',
  buffer: 'BF',
};

// ── Shared sheet chrome ──────────────────────────────────────────

const inputStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  border: `1px solid ${C.line2}`,
  borderRadius: 11,
  background: C.white,
  fontFamily: SANS,
  fontSize: 14.5,
  lineHeight: 1.45,
  color: C.ink,
  padding: '11px 13px',
  outline: 'none',
  resize: 'vertical',
};

function FieldLabel({ children }: { children: ReactNode }) {
  return <div style={mono({ fontSize: 10.5, letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted, marginBottom: 7 })}>{children}</div>;
}

function PrimaryButton({
  children,
  onClick,
  disabled,
  full,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  full?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: full ? '100%' : undefined,
        background: disabled ? C.line3 : C.ink,
        color: disabled ? C.faint : C.white,
        border: 'none',
        borderRadius: 12,
        padding: '12px 18px',
        fontFamily: SANS,
        fontSize: 14.5,
        fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer',
        transition: 'all .12s',
      }}
    >
      {children}
    </button>
  );
}

function GhostButton({
  children,
  onClick,
  full,
  tone,
}: {
  children: ReactNode;
  onClick?: () => void;
  full?: boolean;
  tone?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: full ? '100%' : undefined,
        background: 'none',
        color: tone ?? C.sub,
        border: `1px solid ${C.line3}`,
        borderRadius: 12,
        padding: '11px 16px',
        fontFamily: SANS,
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function StatusBanner({ kind, text }: { kind: 'error' | 'success' | 'info'; text: string }) {
  const tone =
    kind === 'error' ? '#b3261e' : kind === 'success' ? '#3f7a52' : C.sub;
  const dot = kind === 'error' ? '#b3261e' : kind === 'success' ? '#3f7a52' : '#b08968';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 9,
        background: C.chipBg,
        border: `1px solid ${C.line3}`,
        borderRadius: 11,
        padding: '10px 12px',
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot, flexShrink: 0, marginTop: 6 }} />
      <span style={{ fontSize: 13, color: tone, lineHeight: 1.5 }}>{text}</span>
    </div>
  );
}

function Spinner({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: C.faint2, padding: '6px 0' }}>
      <div style={{ display: 'flex', gap: 5 }}>
        {[0, 0.2, 0.4].map((d, i) => (
          <span key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: C.faint, display: 'inline-block', animation: `briefPulse 1.2s infinite ${d}s` }} />
        ))}
      </div>
      <span style={{ fontSize: 13 }}>{label}</span>
    </div>
  );
}

/**
 * The bottom-sheet shell. Mobile-first: full-width, bottom-anchored, rounded top
 * corners, slides up. ≥768px it centers as a ~440px modal. Backdrop closes it.
 */
function SheetShell({
  platform,
  subtitle,
  onClose,
  children,
}: {
  platform: SheetPlatform;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  // Lock body scroll while the sheet is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Esc to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      className="brief-connect-shell"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        background: 'rgba(26,25,22,.34)',
        animation: 'briefUp .2s ease both',
        fontFamily: SANS,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="brief-scroll brief-connect-sheet"
        style={{
          width: '100%',
          maxWidth: 440,
          maxHeight: '90vh',
          overflowY: 'auto',
          background: C.white,
          color: C.ink,
          borderTopLeftRadius: 22,
          borderTopRightRadius: 22,
          // On wider screens the sheet centers and rounds fully (handled below).
          boxShadow: '0 -10px 40px rgba(26,25,22,.18)',
          animation: 'briefSheetUp .26s cubic-bezier(.22,.61,.36,1) both',
          paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
        }}
      >
        {/* drag handle (mobile affordance) */}
        <div className="brief-sheet-handle" style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 2px' }}>
          <span style={{ width: 38, height: 4, borderRadius: 3, background: C.line2 }} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '8px 18px 14px', borderBottom: `1px solid ${C.line4}` }}>
          <div style={{ width: 38, height: 38, borderRadius: 11, background: C.sidebar, border: `1px solid ${C.line2}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={mono({ fontSize: 12.5, fontWeight: 600, color: C.ink2 })}>{PLATFORM_GLYPH[platform]}</span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-.01em' }}>{PLATFORM_LABEL[platform]}</div>
            {subtitle && <div style={mono({ fontSize: 10.5, color: C.faint, marginTop: 2 })}>{subtitle}</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть"
            style={{ width: 38, height: 38, borderRadius: 11, border: 'none', background: 'none', cursor: 'pointer', fontSize: 22, color: C.muted, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '18px' }}>{children}</div>
      </div>
    </div>
  );
}

// Inject the sheet keyframe + desktop centering once (keeps styling self-contained).
function useSheetKeyframes() {
  useEffect(() => {
    const id = 'brief-connect-sheet-style';
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = `
@keyframes briefSheetUp { from { transform: translateY(24px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
@media (min-width: 768px) {
  .brief-connect-shell { align-items: center !important; }
  .brief-connect-sheet { border-radius: 20px !important; max-height: 86vh !important; box-shadow: 0 18px 60px rgba(26,25,22,.22) !important; }
  .brief-connect-sheet .brief-sheet-handle { display: none !important; }
}`;
    document.head.appendChild(el);
  }, []);
}

// ── Credential flow (cookies / API keys) ─────────────────────────

function CredentialFlow({
  platform,
  config,
  onDone,
}: {
  platform: ConnectionPlatform;
  config: CredentialConfig;
  onDone: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<{ kind: 'success' | 'info'; text: string } | null>(null);

  const hasAny = config.fields.some((f) => (values[f.key] ?? '').trim().length > 0);

  const save = async () => {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await connectPlatform(platform, { credentials: values });
      if (config.canValidate) {
        setStatus({ kind: 'info', text: 'Сохранено. Проверяю доступ…' });
        const v = await validateCookies(platform as Platform);
        if (v.ok) {
          const who = v.username ?? v.handle ?? v.name ?? v.vanity;
          setStatus({ kind: 'success', text: who ? `Подключено: @${who}` : 'Подключено и проверено.' });
        } else {
          setError(v.error_message ?? v.error ?? 'Учётные данные сохранены, но проверка не прошла.');
        }
      } else {
        setStatus({ kind: 'success', text: 'Учётные данные сохранены.' });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить.');
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await disconnectPlatform(platform);
      setStatus({ kind: 'info', text: 'Канал отключён.' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось отключить.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {config.note && <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.5 }}>{config.note}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
        {config.fields.map((f) => (
          <div key={f.key}>
            <FieldLabel>{f.label}</FieldLabel>
            {f.multiline ? (
              <textarea
                rows={3}
                value={values[f.key] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                style={{ ...inputStyle, minHeight: 72 }}
              />
            ) : (
              <input
                type={f.secret ? 'password' : 'text'}
                autoComplete="off"
                value={values[f.key] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                style={inputStyle}
              />
            )}
            {f.hint && <div style={mono({ fontSize: 10, color: C.faint, marginTop: 5 })}>{f.hint}</div>}
          </div>
        ))}
      </div>

      {error && <StatusBanner kind="error" text={error} />}
      {status && <StatusBanner kind={status.kind} text={status.text} />}

      {busy ? (
        <Spinner label="Сохраняю…" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <PrimaryButton onClick={save} disabled={!hasAny} full>
            Сохранить и подключить
          </PrimaryButton>
          <div style={{ display: 'flex', gap: 9 }}>
            <GhostButton onClick={disconnect} tone="#b3261e" full>
              Отключить
            </GhostButton>
            <GhostButton onClick={onDone} full>
              Готово
            </GhostButton>
          </div>
        </div>
      )}
    </div>
  );
}

// ── OAuth flow (popup + postMessage) ─────────────────────────────

interface OAuthMessage {
  type: 'summio-publisher-oauth';
  provider?: string;
  connected?: boolean;
  error?: string;
}

function isOAuthMessage(data: unknown): data is OAuthMessage {
  return !!data && typeof data === 'object' && (data as { type?: unknown }).type === 'summio-publisher-oauth';
}

function OAuthFlow({
  platform,
  onDone,
}: {
  platform: OAuthProvider;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<{ kind: 'success' | 'info'; text: string } | null>(null);
  const popupRef = useRef<Window | null>(null);

  // Listen for the origin-checked postMessage from the OAuth callback.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return; // origin check
      if (!isOAuthMessage(event.data)) return;
      const msg = event.data;
      if (msg.provider && msg.provider !== platform) return;
      setBusy(false);
      if (msg.connected) {
        setStatus({ kind: 'success', text: 'Аккаунт подключён через OAuth.' });
      } else {
        setError(msg.error ?? 'OAuth не завершён.');
      }
      popupRef.current?.close();
      popupRef.current = null;
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [platform]);

  const start = () => {
    setError(null);
    setStatus(null);
    setBusy(true);
    const returnTo = `${window.location.origin}${window.location.pathname}`;
    const url = oauthStartUrl(platform, returnTo);
    const popup = window.open(url, 'summio-oauth', 'width=520,height=680,menubar=no,toolbar=no');
    if (!popup) {
      setBusy(false);
      setError('Браузер заблокировал всплывающее окно. Разрешите попапы и повторите.');
      return;
    }
    popupRef.current = popup;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.5 }}>
        Откроется окно авторизации {PLATFORM_LABEL[platform]}. После подтверждения окно закроется автоматически.
      </div>

      {error && <StatusBanner kind="error" text={error} />}
      {status && <StatusBanner kind={status.kind} text={status.text} />}

      {busy ? (
        <Spinner label="Жду подтверждения в окне…" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <PrimaryButton onClick={start} full>
            {status?.kind === 'success' ? 'Переподключить' : 'Войти через OAuth'}
          </PrimaryButton>
          <GhostButton onClick={onDone} full>
            Готово
          </GhostButton>
        </div>
      )}
    </div>
  );
}

// ── Telegram 3-step wizard ───────────────────────────────────────

type TelegramStep = 'token' | 'channels' | 'setup';

function TelegramFlow({ onDone }: { onDone: () => void }) {
  const [step, setStep] = useState<TelegramStep>('token');
  const [token, setToken] = useState('');
  const [botName, setBotName] = useState<string | null>(null);
  const [channels, setChannels] = useState<TelegramSetupChannel[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  // Step 1 — validate token.
  const validateToken = async () => {
    setBusy(true);
    setError(null);
    try {
      const j = await telegramValidateToken(token.trim());
      if (j.ok) {
        setBotName(j.bot?.username ? `@${j.bot.username}` : (j.bot?.first_name ?? 'бот'));
        setStep('channels');
      } else {
        setError(j.error ?? 'Токен не принят.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось проверить токен.');
    } finally {
      setBusy(false);
    }
  };

  // Step 2 — resolve a chat id and add it as a channel.
  const addChannel = async () => {
    const chatId = chatInput.trim();
    if (!chatId) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const chat = await telegramGetChat(token.trim(), chatId);
      if (!chat.ok) {
        setError(chat.error ?? 'Чат не найден или бот не добавлен в канал.');
        return;
      }
      const resolvedId = chat.chat?.id != null ? String(chat.chat.id) : chatId;
      // Persist the channel in the registry, then keep it for setup.
      const reg = await telegramAddChannel(resolvedId);
      if (!reg.ok) {
        setError(reg.error ?? 'Не удалось сохранить канал.');
        return;
      }
      const next: TelegramSetupChannel = {
        id: resolvedId,
        title: chat.chat?.title,
        username: chat.chat?.username,
      };
      setChannels((prev) => (prev.some((c) => c.id === next.id) ? prev : [...prev, next]));
      setChatInput('');
      setStatus(`Добавлен: ${next.title ?? next.username ?? next.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось добавить канал.');
    } finally {
      setBusy(false);
    }
  };

  // Step 3 — register webhook for all collected channels.
  const setupBot = async () => {
    setBusy(true);
    setError(null);
    try {
      const j = await telegramSetupBot(token.trim(), channels);
      if (j.ok) {
        setStatus(
          j.webhook_registered
            ? 'Бот настроен, webhook зарегистрирован.'
            : `Бот настроен${j.webhook_error ? ` (webhook: ${j.webhook_error})` : ''}.`,
        );
        setStep('setup');
      } else {
        setError(j.error ?? 'Не удалось настроить бота.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось настроить бота.');
    } finally {
      setBusy(false);
    }
  };

  const StepDots = () => {
    const order: TelegramStep[] = ['token', 'channels', 'setup'];
    const active = order.indexOf(step);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
        {order.map((_, i) => (
          <span
            key={i}
            style={{
              height: 4,
              flex: 1,
              borderRadius: 3,
              background: i <= active ? C.ink : C.line2,
              transition: 'background .15s',
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <StepDots />

      {step === 'token' && (
        <>
          <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.5 }}>
            Вставьте токен бота из @BotFather. Бот должен быть администратором ваших каналов.
          </div>
          <div>
            <FieldLabel>Токен бота</FieldLabel>
            <input
              type="password"
              autoComplete="off"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="123456:ABC-DEF…"
              style={inputStyle}
            />
          </div>
          {error && <StatusBanner kind="error" text={error} />}
          {busy ? (
            <Spinner label="Проверяю токен…" />
          ) : (
            <PrimaryButton onClick={validateToken} disabled={token.trim().length === 0} full>
              Проверить токен
            </PrimaryButton>
          )}
        </>
      )}

      {step === 'channels' && (
        <>
          {botName && <StatusBanner kind="success" text={`Бот: ${botName}`} />}
          <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.5 }}>
            Добавьте каналы по @username или числовому chat_id. Бот должен быть в них администратором.
          </div>
          <div>
            <FieldLabel>Канал</FieldLabel>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (!busy) void addChannel();
                  }
                }}
                placeholder="@mychannel или -100…"
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                type="button"
                onClick={() => void addChannel()}
                disabled={busy || chatInput.trim().length === 0}
                style={{
                  flexShrink: 0,
                  border: 'none',
                  borderRadius: 11,
                  padding: '0 16px',
                  background: busy || chatInput.trim().length === 0 ? C.line3 : C.ink,
                  color: busy || chatInput.trim().length === 0 ? C.faint : C.white,
                  fontFamily: SANS,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: busy || chatInput.trim().length === 0 ? 'default' : 'pointer',
                }}
              >
                +
              </button>
            </div>
          </div>

          {channels.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {channels.map((ch) => (
                <div key={ch.id} style={{ display: 'flex', alignItems: 'center', gap: 9, border: `1px solid ${C.line}`, borderRadius: 11, padding: '10px 12px', background: C.white }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#3f7a52', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ch.title ?? (ch.username ? `@${ch.username}` : ch.id)}
                    </div>
                    <div style={mono({ fontSize: 10, color: C.faint })}>{ch.id}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setChannels((prev) => prev.filter((c) => c.id !== ch.id))}
                    aria-label="Убрать из списка"
                    style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: C.faint, lineHeight: 1 }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {error && <StatusBanner kind="error" text={error} />}
          {status && <StatusBanner kind="info" text={status} />}
          {busy && <Spinner label="Проверяю канал…" />}

          {!busy && (
            <div style={{ display: 'flex', gap: 9 }}>
              <GhostButton onClick={() => setStep('token')} full>
                Назад
              </GhostButton>
              <PrimaryButton onClick={setupBot} disabled={channels.length === 0} full>
                Далее
              </PrimaryButton>
            </div>
          )}
        </>
      )}

      {step === 'setup' && (
        <>
          {status && <StatusBanner kind="success" text={status} />}
          <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.5 }}>
            {channels.length} {channels.length === 1 ? 'канал готов' : 'канала(ов) готовы'} к публикации через Telegram.
          </div>
          {error && <StatusBanner kind="error" text={error} />}
          <PrimaryButton onClick={onDone} full>
            Готово
          </PrimaryButton>
        </>
      )}
    </div>
  );
}

// ── OpenAI / Codex device flow ───────────────────────────────────

const CODEX_POLL_MS = 3000;
const CODEX_CAP_MS = 15 * 60 * 1000;

type CodexPhase = 'idle' | 'starting' | 'waiting' | 'complete' | 'error';

function CodexFlow({ onDone }: { onDone: () => void }) {
  const [phase, setPhase] = useState<CodexPhase>('idle');
  const [session, setSession] = useState<CodexDeviceStartResult | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [account, setAccount] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const deadlineRef = useRef<number>(0);
  const sessionRef = useRef<CodexDeviceStartResult | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const applyStatus = useCallback(
    (finalize: boolean) => async () => {
      const s = sessionRef.current;
      if (!s) return;
      if (!finalize && Date.now() > deadlineRef.current) {
        stopPolling();
        setPhase('error');
        setError('Время авторизации истекло. Начните заново.');
        return;
      }
      try {
        const r = await codexDeviceStatus(s.session_id, s.started_at, finalize);
        if (r.code === 'codex_device_session_expired') {
          stopPolling();
          setPhase('error');
          setError('Сессия авторизации устарела. Начните заново.');
          return;
        }
        setMessage(r.message ?? null);
        if (r.status === 'complete') {
          stopPolling();
          setAccount(r.account_id ?? null);
          setPhase('complete');
        } else if (r.status === 'error') {
          stopPolling();
          setPhase('error');
          setError(r.error ?? r.message ?? 'Авторизация не завершилась.');
        }
        // 'starting' | 'waiting' → keep polling.
      } catch (e) {
        stopPolling();
        setPhase('error');
        setError(e instanceof Error ? e.message : 'Ошибка проверки статуса.');
      }
    },
    [stopPolling],
  );

  const start = useCallback(async () => {
    setError(null);
    setMessage(null);
    setAccount(null);
    setPhase('starting');
    try {
      const s = await codexDeviceStart();
      sessionRef.current = s;
      setSession(s);
      deadlineRef.current = Date.now() + CODEX_CAP_MS;
      setPhase('waiting');
      stopPolling();
      pollRef.current = setInterval(() => {
        void applyStatus(false)();
      }, CODEX_POLL_MS);
      // Kick an immediate check so the user isn't waiting a full interval.
      void applyStatus(false)();
    } catch (e) {
      setPhase('error');
      setError(e instanceof Error ? e.message : 'Не удалось запустить авторизацию.');
    }
  }, [applyStatus, stopPolling]);

  const finalize = useCallback(() => {
    void applyStatus(true)();
  }, [applyStatus]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.5 }}>
        Авторизация подписки Codex по коду устройства. Откройте ссылку, введите код и подтвердите доступ.
      </div>

      {phase === 'idle' && (
        <PrimaryButton onClick={() => void start()} full>
          Начать авторизацию
        </PrimaryButton>
      )}

      {phase === 'starting' && <Spinner label="Создаю сессию…" />}

      {(phase === 'waiting' || phase === 'complete') && session && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
          {session.user_code && (
            <div style={{ border: `1px solid ${C.line}`, borderRadius: 14, padding: '15px 16px', background: C.sidebar, textAlign: 'center' }}>
              <div style={mono({ fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: C.faint, marginBottom: 8 })}>Код устройства</div>
              <div style={mono({ fontSize: 26, fontWeight: 700, letterSpacing: '.14em', color: C.ink })}>{session.user_code}</div>
            </div>
          )}
          <a
            href={session.activation_url}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'block',
              textAlign: 'center',
              textDecoration: 'none',
              background: C.ink,
              color: C.white,
              borderRadius: 12,
              padding: '12px 18px',
              fontSize: 14.5,
              fontWeight: 600,
            }}
          >
            Открыть страницу авторизации
          </a>
        </div>
      )}

      {phase === 'waiting' && (
        <>
          <Spinner label={message ?? 'Жду подтверждения…'} />
          <GhostButton onClick={finalize} full>
            Завершить
          </GhostButton>
        </>
      )}

      {phase === 'complete' && (
        <StatusBanner kind="success" text={account ? `Подключено. Аккаунт: ${account}` : 'Codex подключён.'} />
      )}

      {error && <StatusBanner kind="error" text={error} />}

      {(phase === 'error') && (
        <PrimaryButton onClick={() => void start()} full>
          Начать заново
        </PrimaryButton>
      )}

      {(phase === 'complete' || phase === 'error') && (
        <GhostButton onClick={onDone} full>
          Готово
        </GhostButton>
      )}
    </div>
  );
}

// ── Buffer read-only channel directory ───────────────────────────

function BufferFlow({ onDone }: { onDone: () => void }) {
  const [orgs, setOrgs] = useState<BufferOrganization[] | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [channels, setChannels] = useState<BufferChannel[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const [loadingChannels, setLoadingChannels] = useState(false);

  const handleError = (e: unknown) => {
    if (e instanceof BufferRateLimitError) {
      setRetryAfter(e.retryAfterSeconds ?? null);
      setError(e.bufferError ?? 'Buffer ограничил частоту запросов. Повторите позже.');
    } else {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить данные Buffer.');
    }
  };

  // Load organizations once.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await bufferOrganizations();
        if (!alive) return;
        setOrgs(list);
        if (list.length > 0) setOrgId(list[0].id);
      } catch (e) {
        if (alive) handleError(e);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Load channels for the selected organization.
  useEffect(() => {
    if (!orgId) return;
    let alive = true;
    setLoadingChannels(true);
    setChannels(null);
    setError(null);
    setRetryAfter(null);
    (async () => {
      try {
        const list = await bufferChannels(orgId);
        if (!alive) return;
        setChannels(list);
      } catch (e) {
        if (alive) handleError(e);
      } finally {
        if (alive) setLoadingChannels(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [orgId]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.5 }}>
        Подключение Buffer доступно только для чтения — каналы и состояние очереди берутся из вашего аккаунта Buffer.
      </div>

      {orgs && orgs.length > 1 && (
        <div>
          <FieldLabel>Организация</FieldLabel>
          <div className="brief-scroll-hidden" style={{ display: 'flex', gap: 7, overflowX: 'auto', paddingBottom: 2 }}>
            {orgs.map((o) => {
              const active = o.id === orgId;
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setOrgId(o.id)}
                  style={{
                    flexShrink: 0,
                    borderRadius: 10,
                    padding: '8px 13px',
                    fontFamily: SANS,
                    fontSize: 13,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    ...(active
                      ? { background: C.ink, color: C.white, border: `1px solid ${C.ink}`, fontWeight: 600 }
                      : { background: 'transparent', color: C.sub, border: `1px solid ${C.line2}` }),
                  }}
                >
                  {o.name || o.id}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {error && (
        <StatusBanner
          kind="error"
          text={retryAfter != null ? `${error} (повтор через ~${retryAfter} с)` : error}
        />
      )}

      {!orgs && !error && <Spinner label="Загружаю организации Buffer…" />}
      {orgs && orgs.length === 0 && !error && (
        <div style={{ fontSize: 13.5, color: C.muted }}>В аккаунте Buffer нет организаций.</div>
      )}

      {loadingChannels && <Spinner label="Загружаю каналы…" />}

      {channels && channels.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {channels.map((ch) => {
            const paused = ch.isQueuePaused === true;
            return (
              <div key={ch.id} style={{ display: 'flex', alignItems: 'center', gap: 10, border: `1px solid ${C.line}`, borderRadius: 12, padding: '11px 13px', background: C.white }}>
                <div style={{ width: 30, height: 30, borderRadius: 9, background: C.sidebar, border: `1px solid ${C.line2}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                  {ch.avatar ? (
                    <img src={ch.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={mono({ fontSize: 10, color: C.muted })}>{ch.service.slice(0, 2).toUpperCase()}</span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ch.displayName ?? ch.name ?? ch.id}
                  </div>
                  <div style={mono({ fontSize: 10, color: C.faint })}>{ch.service}</div>
                </div>
                <span
                  style={{
                    fontSize: 11.5,
                    color: paused ? '#b08968' : '#3f7a52',
                    background: C.chipBg,
                    borderRadius: 8,
                    padding: '4px 9px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {paused ? 'Очередь на паузе' : 'Готов'}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {channels && channels.length === 0 && !loadingChannels && (
        <div style={{ fontSize: 13.5, color: C.muted }}>В этой организации нет каналов.</div>
      )}

      <GhostButton onClick={onDone} full>
        Готово
      </GhostButton>
    </div>
  );
}

// ── Router ───────────────────────────────────────────────────────

export interface ConnectSheetProps {
  platform: SheetPlatform;
  onClose: () => void;
  /** Called after a successful connect/disconnect so the caller can refresh. */
  onDone: () => void;
}

/**
 * The single connect sheet. Picks the right flow for `platform` and renders it
 * inside the bottom-sheet shell. `onDone` fires when the user finishes (so the
 * Каналы screen can re-fetch connections/readiness); `onClose` just dismisses.
 */
export function ConnectSheet({ platform, onClose, onDone }: ConnectSheetProps) {
  useSheetKeyframes();

  const finish = useCallback(() => {
    onDone();
    onClose();
  }, [onDone, onClose]);

  let body: ReactNode;
  let subtitle: string | undefined;

  if (platform === 'buffer') {
    subtitle = 'только чтение';
    body = <BufferFlow onDone={finish} />;
  } else if (platform === 'openai') {
    subtitle = 'device auth';
    body = <CodexFlow onDone={finish} />;
  } else if (platform === 'telegram') {
    subtitle = 'бот · 3 шага';
    body = <TelegramFlow onDone={finish} />;
  } else if (platform === 'twitter') {
    // Twitter is surfaced as OAuth-first. The other OAuth-capable platforms fall
    // through to their cookie/credential forms below.
    subtitle = 'OAuth';
    body = <OAuthFlow platform={platform} onDone={finish} />;
  } else {
    const config = CREDENTIAL_CONFIGS[platform];
    subtitle = config?.canValidate ? 'учётные данные' : 'ключ доступа';
    body = config ? (
      <CredentialFlow platform={platform} config={config} onDone={finish} />
    ) : (
      <div style={{ fontSize: 13.5, color: C.muted }}>Для этой платформы пока нет формы подключения.</div>
    );
  }

  return (
    <SheetShell platform={platform} subtitle={subtitle} onClose={onClose}>
      {body}
    </SheetShell>
  );
}

// ── Open-sheet helper for the Каналы screen ──────────────────────

export interface ConnectSheetController {
  /** Currently open sheet target, or null when closed. */
  active: SheetPlatform | null;
  /** Open a sheet for a platform. */
  open: (platform: SheetPlatform) => void;
  /** Close the current sheet. */
  close: () => void;
  /**
   * Render the active sheet. Pass `onDone` to be notified when a connect/
   * disconnect completes (e.g. to refetch). Returns null when nothing is open.
   */
  render: (onDone?: () => void) => ReactNode;
}

/**
 * Small hook the Каналы screen can use to drive connect sheets without owning
 * any of the flow logic. Example:
 *
 *   const sheet = useConnectSheet();
 *   <button onClick={() => sheet.open('telegram')}>Подключить</button>
 *   {sheet.render(() => reload())}
 */
export function useConnectSheet(): ConnectSheetController {
  const [active, setActive] = useState<SheetPlatform | null>(null);

  const open = useCallback((platform: SheetPlatform) => setActive(platform), []);
  const close = useCallback(() => setActive(null), []);

  const render = useCallback(
    (onDone?: () => void): ReactNode => {
      if (!active) return null;
      return (
        <ConnectSheet
          platform={active}
          onClose={() => setActive(null)}
          onDone={() => onDone?.()}
        />
      );
    },
    [active],
  );

  return { active, open, close, render };
}

export default ConnectSheet;
